import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Container,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Article, ArrowForward, Person, CalendarToday } from "@mui/icons-material";
import eventos from "../data/eventos.js";

export const Eventos = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const handleCardClick = (id) => {
    navigate(`/eventos/${id}`);
  };

  const formatDate = (dateString) => {
    // Se a string contém "a" (indicando um período), retorna como string
    if (typeof dateString === 'string' && (dateString.includes(' a ') || dateString.includes('-'))) {
      return dateString;
    }
    
    // Caso contrário, tenta formatar como data
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Se não for uma data válida, retorna como string
      }
      return date.toLocaleDateString("pt-BR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString; // Em caso de erro, retorna a string original
    }
  };

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f8f9fa", 
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Cabeçalho */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Article sx={{ fontSize: { xs: 32, md: 40 } }} />
            Eventos
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
              px: { xs: 2, md: 0 },
            }}
          >
            Fique por dentro de todos os eventos que estão acontecendo para sua edificação.
          </Typography>
        </Box>

        {/* Grid de Eventos */}
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ justifyContent: "center" }}>
          {eventos.map((evento) => (
            <Grid item xs={12} sm={6} lg={4} key={evento.id}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  border: "1px solid #e0e0e0",
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  maxWidth: "380px",
                  mx: "auto",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: `0 8px 30px ${theme.palette.primary.dark}`,
                  },
                }}
                onClick={() => handleCardClick(evento.id)}
              >
                {/* Imagem */}
                <CardMedia
                  component="img"
                  height={isMobile ? "200" : "220"}
                  image={evento.imagem}
                  alt={evento.titulo}
                  sx={{
                    objectFit: "cover",
                    backgroundColor: "#f0f0f0",
                  }}
                  onError={(e) => {
                    e.target.src = `data:image/svg+xml;base64,${btoa(`
                      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                        <rect width="100%" height="100%" fill="${theme.palette.primary.main}"/>
                        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" 
                              fill="white" text-anchor="middle" dy="0.3em">
                          Imagem não encontrada
                        </text>
                      </svg>
                    `)}`;
                  }}
                />
                
                {/* Conteúdo */}
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  {/* Meta informações */}
                  <Box 
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2, 
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {formatDate(evento.data)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Person sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {evento.autor}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Título */}
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      color: "#333",
                      mb: 1.5,
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "2.6em",
                    }}
                  >
                    {evento.titulo}
                  </Typography>

                  {/* Resumo */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 3,
                      lineHeight: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minHeight: "4.5em",
                    }}
                  >
                    {evento.resumo}
                  </Typography>

                  {/* Link "Leia mais" */}
                  <Button
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                      textTransform: "none",
                      p: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Leia mais
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Mensagem se não houver eventos */}
        {eventos.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum evento disponível no momento.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};